/**
 * Always-on debugger module. Hooks `window.error` and `unhandledrejection`,
 * then ships each fresh runtime error as a synthetic FeedbackReport via
 * the existing widget submit pipeline.
 *
 * Same opt-in pattern as `withReplay` / `withWebVitals` — host apps that
 * want curated-feedback-only simply don't import it.
 *
 * Design choices (see PR description):
 *  - Per-fingerprint cooldown (default 5 min) absorbs render loops without
 *    flooding the operator dashboard or burning the per-key write throttle.
 *  - Server-side fingerprinting groups across sessions; the client-side
 *    fingerprint exists only to dedup within a single page lifetime, so
 *    it doesn't need to match the server's hash.
 *  - Recursion guard: an error thrown *inside* the submit pipeline must
 *    not re-enter the handler.
 *  - Description is the error message truncated at 200 chars — the full
 *    stack lives in technical_context.errors[].
 *  - No screenshot is taken (buildAndSubmit honors `synthetic: true`):
 *    html2canvas is slow + the DOM is unreliable mid-error.
 */

import type { FeedbackApi } from '../types'

export interface ErrorTrackingOptions {
  /** Opt-out kill switch without removing the import. Default `true`. */
  enabled?: boolean
  /**
   * Drop repeats of the same fingerprint within this window.
   * Default 300_000 ms (5 minutes). Set to 0 to disable dedup
   * (every fired error becomes a report — useful only for tests).
   */
  perFingerprintCooldownMs?: number
  /**
   * Probabilistic head sampling: 1.0 captures everything, 0.5 drops half.
   * Default 1.0. Sampled-out errors don't count toward the cooldown — if
   * you sample at 0.1, you'll still see ~10% of the volume per fingerprint.
   */
  sampleRate?: number
  /** Max characters in the auto-generated description. Default 200. */
  maxDescriptionLen?: number
  /**
   * Global ceiling on synthetic submissions per minute, regardless of
   * fingerprint. Defends against a hostile / buggy page that throws
   * errors with attacker-varying `message` strings (counter, nonce) —
   * each unique message creates a fresh fingerprint, bypassing the
   * per-fingerprint cooldown, and would otherwise burn the project's
   * backend write quota.
   * Default 30. Set to 0 to disable the global cap (not recommended).
   */
  globalRatePerMinute?: number
  /**
   * Max number of distinct fingerprints emitted per page lifetime. Once
   * exceeded, NEW fingerprints are dropped (the cooldown still applies
   * to already-seen ones, so legitimate repeating errors keep cycling).
   * Defense against a page that synthesizes an unbounded number of
   * unique error shapes.
   * Default 200. Set to 0 to disable.
   */
  maxDistinctFingerprints?: number
}

interface InternalApi extends FeedbackApi {
  _registerTransformer?: (fn: unknown) => void
  /** Set once withErrorTracking has armed this instance (idempotency guard). */
  _errorTrackingArmed?: boolean
}

interface NormalizedError {
  name: string
  message: string
  stack?: string
}

const DEFAULTS = {
  enabled: true,
  perFingerprintCooldownMs: 5 * 60 * 1000,
  sampleRate: 1,
  maxDescriptionLen: 200,
  globalRatePerMinute: 30,
  maxDistinctFingerprints: 200,
} as const satisfies Required<ErrorTrackingOptions>

/**
 * Pull a normalized {name, message, stack} from whatever the runtime
 * handed us. Browsers throw genuine Error subclasses for uncaught
 * exceptions but `unhandledrejection.reason` is sometimes a string,
 * a plain object, or undefined.
 */
function normalize(thrown: unknown): NormalizedError {
  if (thrown instanceof Error) {
    return {
      name: thrown.name || 'Error',
      message: String(thrown.message ?? ''),
      ...(thrown.stack && { stack: String(thrown.stack) }),
    }
  }
  if (typeof thrown === 'string') return { name: 'Error', message: thrown }
  if (thrown && typeof thrown === 'object') {
    const o = thrown as Record<string, unknown>
    return {
      name: (typeof o.name === 'string' && o.name) || 'Error',
      message:
        (typeof o.message === 'string' && o.message) ||
        (() => {
          try { return JSON.stringify(thrown) } catch { return String(thrown) }
        })(),
      ...(typeof o.stack === 'string' && { stack: o.stack }),
    }
  }
  return { name: 'Error', message: String(thrown) }
}

/**
 * Stable per-session signature. Server still computes its own fingerprint
 * across sessions — this only needs to dedup within one page lifetime.
 * Stack frames are the strongest discriminator; pathname keeps two distinct
 * routes from collapsing onto the same key.
 *
 * SECURITY: we include only a 30-char message prefix, not the full message.
 * A hostile or buggy page that throws errors with counter-suffixed messages
 * (e.g. `Error 1`, `Error 2`, …) would otherwise produce a fresh fingerprint
 * per throw and bypass the per-fingerprint cooldown. The stack frames are
 * the same across those counter variants, so collapsing the message into a
 * prefix preserves real-error distinction while killing the counter bypass.
 */
function clientFingerprint(err: NormalizedError, pathname: string): string {
  const firstFrames = (err.stack ?? '').split('\n').slice(0, 4).join('\n')
  const messagePrefix = err.message.slice(0, 30)
  return `${err.name}:${messagePrefix}|${pathname}|${firstFrames}`
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return s.slice(0, Math.max(0, max - 1)) + '…'
}

export function withErrorTracking(
  fb: FeedbackApi,
  options: ErrorTrackingOptions = {},
): FeedbackApi {
  const opts = { ...DEFAULTS, ...options }
  if (!opts.enabled) return fb
  if (typeof window === 'undefined') return fb

  const internal = fb as InternalApi

  // Idempotency guard. The same instance can now be armed from two places:
  // the host-side <FeedbackProvider> (opt-out default) AND the runtime
  // widget bundle (which self-arms on the loader path). A client who both
  // upgrades the provider and receives the self-arming bundle would
  // otherwise double-register the window listeners and file every error
  // twice. First arm wins; later calls are no-ops.
  if (internal._errorTrackingArmed) return fb
  internal._errorTrackingArmed = true

  // Recursion guard, scoped per-fingerprint: while we're posting a synthetic
  // report for fingerprint X, a re-entry for the SAME X (e.g. an error
  // raised by the submit pipeline itself) is dropped. Distinct errors that
  // fire concurrently still both go through.
  const inFlight = new Set<string>()

  // fingerprint -> last-sent epoch ms. Periodically pruned in shouldSend().
  const lastSent = new Map<string, number>()
  // Sliding-window of recent send timestamps for the global rate cap.
  // Trimmed in shouldSend(); never grows beyond globalRatePerMinute entries.
  const recentSendTimes: number[] = []

  function shouldSend(fp: string, now: number): boolean {
    // (1) Global rate ceiling — runs first because it doesn't depend on
    //     fingerprint state. Defends against the "counter-suffixed error
    //     messages" attack that would otherwise produce one unique
    //     fingerprint per throw.
    if (opts.globalRatePerMinute > 0) {
      const windowStart = now - 60_000
      // Drop expired entries from the front of the sliding window.
      while (recentSendTimes.length > 0 && recentSendTimes[0]! < windowStart) {
        recentSendTimes.shift()
      }
      if (recentSendTimes.length >= opts.globalRatePerMinute) {
        return false
      }
    }
    // (2) Per-page cap on the number of distinct fingerprints. A hostile
    //     page that synthesizes an unbounded number of unique error
    //     shapes runs out of new slots after this many — already-seen
    //     fingerprints keep cycling under the per-fp cooldown.
    if (
      opts.maxDistinctFingerprints > 0 &&
      !lastSent.has(fp) &&
      lastSent.size >= opts.maxDistinctFingerprints
    ) {
      return false
    }
    if (opts.perFingerprintCooldownMs <= 0) {
      recentSendTimes.push(now)
      return true
    }
    const prev = lastSent.get(fp)
    if (prev !== undefined && now - prev < opts.perFingerprintCooldownMs) {
      return false
    }
    // Cheap stale-entry sweep so the Map doesn't grow unboundedly on a
    // long-lived session with many distinct errors. First pass: drop
    // entries past `cooldown * 2`. If we're still above a hard cap of
    // 256 — i.e. an SPA that's been live for hours and keeps emitting
    // *fresh* fingerprints faster than they go stale — drop the oldest
    // entries until we're back to a comfortable ceiling. Map iteration
    // order is insertion order so the first N keys are the oldest.
    const HARD_CAP = 256
    const TARGET_AFTER_TRIM = 192
    if (lastSent.size > HARD_CAP) {
      const cutoff = now - opts.perFingerprintCooldownMs * 2
      for (const [k, v] of lastSent) {
        if (v < cutoff) lastSent.delete(k)
      }
      if (lastSent.size > HARD_CAP) {
        const toDrop = lastSent.size - TARGET_AFTER_TRIM
        let dropped = 0
        for (const k of lastSent.keys()) {
          if (dropped >= toDrop) break
          lastSent.delete(k)
          dropped++
        }
      }
    }
    recentSendTimes.push(now)
    return true
  }

  async function report(err: NormalizedError) {
    if (opts.sampleRate < 1 && Math.random() >= opts.sampleRate) return

    const fp = clientFingerprint(err, window.location.pathname)
    if (inFlight.has(fp)) return
    const now = Date.now()
    if (!shouldSend(fp, now)) return
    lastSent.set(fp, now)

    const description = truncate(
      err.message ? `${err.name}: ${err.message}` : err.name,
      opts.maxDescriptionLen,
    )

    inFlight.add(fp)
    try {
      await internal.submit({
        description,
        feedback_type: 'bug',
        severity: 'high',
        synthetic: true,
      } as Parameters<FeedbackApi['submit']>[0] & { synthetic: boolean })
    } catch {
      // Swallow submit errors — propagating them would just trigger another
      // unhandledrejection and feed the loop. The host's `onError` callback
      // (configured on createFeedback) still fires for visibility.
    } finally {
      inFlight.delete(fp)
    }
  }

  function onError(event: ErrorEvent) {
    const candidate: unknown = event.error ?? {
      name: 'Error',
      message: event.message,
      stack: `at ${event.filename}:${event.lineno}:${event.colno}`,
    }
    void report(normalize(candidate))
  }

  function onUnhandledRejection(event: PromiseRejectionEvent) {
    void report(normalize(event.reason))
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)

  // Wrap shutdown so listeners come down with the widget. Without this, a
  // host that re-mounts the widget under React StrictMode would double the
  // listeners and emit each error twice.
  const originalShutdown = internal.shutdown.bind(internal)
  internal.shutdown = () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
    lastSent.clear()
    originalShutdown()
  }

  return fb
}
